import * as XLSX from "xlsx";

import { Button, ButtonType, Card, Dropdown, ImportModal } from "../../Components";
import React, { useCallback, useEffect, useState } from "react";

import { AdOpsToolProps } from "../AdOps";
import { Spinner } from "react-bootstrap";
import { StreamingUtilsLambdaFetch } from "../../utils/fetch-utils";

enum Dial {
  MAPPING = "MAPPING",
  IGNORE = "IGNORE",
}

const DERIVED_ID_PATTERN = "(BPM[A-Z0-9]+ID[0-9]+)";
const ISCI_PATTERN = "([A-Z]{2}[A-Za-z0-9]{2,}?ENH)";
const DISPLAY_PATTERN = "(.*?[0-9]{2,4}x[0-9]{2,4}.*)";

interface Mapping {
  bpm_id: string;
  partner_id: string;
}

interface Ignore {
  dsp: string;
  placement_name: string;
  placement_id: string;
}

const patternMatch = (input: string, pattern: string): string => {
  const regex = new RegExp(pattern);
  const match = input.trim().match(regex);
  return match ? match[1] : "";
};

const addToMap = (map: Map<String, String[]>, bpmId: String, partnerId: String) => {
  const existingPartnerIds: String[] | undefined = map.get(bpmId);
  existingPartnerIds && existingPartnerIds.length
    ? map.set(bpmId, [...existingPartnerIds, partnerId.trim()])
    : map.set(bpmId, [partnerId.trim()]);
};

const processMappingRows = (
  rows: Mapping[],
  derivedIdMappings: Map<String, String[]>,
  isciMappings: Map<String, String[]>,
  failedMappings: String[]
): {
  tempDerivedIdMappings: Map<String, String[]>;
  tempIsciMappings: Map<String, String[]>;
  tempFailedMappings: String[];
} => {
  const tempDerivedIdMappings: Map<String, String[]> = new Map(derivedIdMappings);
  const tempIsciMappings: Map<String, String[]> = new Map(isciMappings);
  const tempFailedMappings: String[] = [...failedMappings];
  rows.forEach(row => {
    if (!row.bpm_id && !row.partner_id) {
      return;
    }
    if (!row.bpm_id || !row.partner_id) {
      tempFailedMappings.push(`${row.bpm_id}: ${row.partner_id}`);
      return;
    }
    const derivedId = patternMatch(String(row.bpm_id), DERIVED_ID_PATTERN);
    const isci = patternMatch(String(row.bpm_id), ISCI_PATTERN);
    const displayId = patternMatch(String(row.bpm_id), DISPLAY_PATTERN);
    if (derivedId) {
      addToMap(tempDerivedIdMappings, derivedId, String(row.partner_id));
    } else if (isci) {
      addToMap(tempIsciMappings, isci, String(row.partner_id));
    } else if (displayId) {
      addToMap(tempIsciMappings, displayId, String(row.partner_id));
    } else {
      tempFailedMappings.push(`${row.bpm_id}: ${row.partner_id}`);
    }
  });
  return {
    tempDerivedIdMappings,
    tempIsciMappings,
    tempFailedMappings,
  };
};

const processIgnoreRows = (
  rows: Ignore[],
  tradeDeskIgnores: Map<String, String[]>,
  amazonIgnores: Map<String, String[]>,
  failedIgnores: String[]
): {
  tempTradeDeskIgnores: Map<String, String[]>;
  tempAmazonIgnores: Map<String, String[]>;
  tempFailedIgnores: String[];
} => {
  const tempTradeDeskIgnores: Map<String, String[]> = new Map(tradeDeskIgnores);
  const tempAmazonIgnores: Map<String, String[]> = new Map(amazonIgnores);
  const tempFailedIgnores: String[] = [...failedIgnores];
  rows.forEach(row => {
    if (!row.dsp && !row.placement_name && !row.placement_id) {
      return;
    }
    if (!row.dsp || !row.placement_name || !row.placement_id) {
      tempFailedIgnores.push(`${row.dsp} - ${row.placement_name}: ${row.placement_id}`);
      return;
    }
    if (String(row.dsp).trim().toLowerCase() === "ttd") {
      addToMap(tempTradeDeskIgnores, String(row.placement_name), String(row.placement_id));
    } else if (String(row.dsp).toLowerCase() === "amazon") {
      addToMap(tempAmazonIgnores, String(row.placement_name), String(row.placement_id));
    } else {
      tempFailedIgnores.push(`${row.dsp} - ${row.placement_name}: ${row.placement_id}`);
    }
  });
  return {
    tempTradeDeskIgnores,
    tempAmazonIgnores,
    tempFailedIgnores,
  };
};

const StreamingMappingAdder: React.FC<AdOpsToolProps> = ({ utils }) => {
  const [activeAPICall, setActiveAPICall] = useState(false);
  const [dial, setDial] = useState<Dial.MAPPING | Dial.IGNORE | null>(null);
  const { setError } = utils;
  const [showBulkImportModal, setShowBulkImportModal] = useState(false);
  const [file, setFile] = useState<File | undefined>();
  const [derivedIdMappings, setDerivedIdMappings] = useState<Map<String, String[]>>(new Map());
  const [isciMappings, setIsciMappings] = useState<Map<String, String[]>>(new Map());
  const [failedMappings, setFailedMappings] = useState<String[]>([]);
  const [tradeDeskIgnores, setTradeDeskIgnores] = useState<Map<String, String[]>>(new Map());
  const [amazonIgnores, setAmazonIgnores] = useState<Map<String, String[]>>(new Map());
  const [failedIgnores, setFailedIgnores] = useState<String[]>([]);

  const addStreamingMapping = useCallback(async () => {
    try {
      setActiveAPICall(true);
      await StreamingUtilsLambdaFetch("/streamingMappingAdder", {
        method: "POST",
        body: JSON.stringify({
          derivedIdMappings: Object.fromEntries(derivedIdMappings),
          isciMappings: Object.fromEntries(isciMappings),
          failedMappings,
        }),
      });
      if (failedMappings.length) {
        setError({
          message: `The following mappings could not be parsed. All other mappings successfully entered. \n ${failedMappings}`,
          reportError: new Error("Failed mappings"),
        });
      }
      setDerivedIdMappings(new Map());
      setIsciMappings(new Map());
      setFailedMappings([]);
      setFile(undefined);
      setDial(null);
    } catch (e) {
      setError({
        message: e instanceof Error ? e.message : String(e),
        reportError: e,
      });
    }
    setActiveAPICall(false);
  }, [derivedIdMappings, isciMappings, failedMappings, setError]);

  const addStreamingIgnore = useCallback(async () => {
    try {
      setActiveAPICall(true);
      await StreamingUtilsLambdaFetch("/streamingIgnoreAdder", {
        method: "POST",
        body: JSON.stringify({
          tradeDeskIgnores: Object.fromEntries(tradeDeskIgnores),
          amazonIgnores: Object.fromEntries(amazonIgnores),
          failedIgnores,
        }),
      });
      if (failedIgnores.length) {
        setError({
          message: `The following ignored placements could not be parsed. All other ignores successfully entered. \n ${failedIgnores}`,
          reportError: new Error("Failed ignores"),
        });
      }
      setTradeDeskIgnores(new Map());
      setAmazonIgnores(new Map());
      setFailedIgnores([]);
      setFile(undefined);
      setDial(null);
    } catch (e) {
      setError({
        message: e instanceof Error ? e.message : String(e),
        reportError: e,
      });
    }
    setActiveAPICall(false);
  }, [tradeDeskIgnores, amazonIgnores, failedIgnores, setError]);

  const importMappingData = useCallback(() => {
    try {
      if (file) {
        const fileReader = new FileReader();
        fileReader.onload = event => {
          if (event.target) {
            const arrayBuffer = event.target.result as ArrayBuffer;
            const workbook = XLSX.read(arrayBuffer, { type: "array" });
            const worksheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[worksheetName];
            if (dial === Dial.MAPPING) {
              const rows: Mapping[] = XLSX.utils.sheet_to_json(worksheet);
              const {
                tempDerivedIdMappings,
                tempIsciMappings,
                tempFailedMappings,
              } = processMappingRows(rows, derivedIdMappings, isciMappings, failedMappings);
              setDerivedIdMappings(tempDerivedIdMappings);
              setIsciMappings(tempIsciMappings);
              setFailedMappings(tempFailedMappings);
            } else if (dial === Dial.IGNORE) {
              const rows: Ignore[] = XLSX.utils.sheet_to_json(worksheet);
              const {
                tempTradeDeskIgnores,
                tempAmazonIgnores,
                tempFailedIgnores,
              } = processIgnoreRows(rows, tradeDeskIgnores, amazonIgnores, failedIgnores);
              setTradeDeskIgnores(tempTradeDeskIgnores);
              setAmazonIgnores(tempAmazonIgnores);
              setFailedIgnores(tempFailedIgnores);
            }
          }
        };
        fileReader.readAsArrayBuffer(file);
      }
    } catch (e) {
      const reportError = e as Error;
      setError({
        message: `Failed to import: ${reportError.message}`,
        reportError,
      });
    }
  }, [
    dial,
    file,
    setError,
    derivedIdMappings,
    isciMappings,
    failedMappings,
    tradeDeskIgnores,
    amazonIgnores,
    failedIgnores,
  ]);

  useEffect(() => {
    if (failedMappings.length && !derivedIdMappings.size && !isciMappings.size && !activeAPICall) {
      setError({
        message:
          "All provided mappings could not be parsed. Please double check your file structure. Columns should be: bpm_id, partner_id",
        reportError: new Error("All provided mappings could not be parsed"),
      });
    }
  }, [setError, failedMappings, derivedIdMappings, isciMappings, activeAPICall]);

  useEffect(() => {
    if (failedIgnores.length && !tradeDeskIgnores.size && !amazonIgnores.size && !activeAPICall) {
      setError({
        message:
          "All provided ignores could not be parsed. Please double check your file structure. Columns should be: dsp, placement_name, placement_id",
        reportError: new Error("All provided ignores could not be parsed"),
      });
    }
  }, [setError, failedIgnores, tradeDeskIgnores, amazonIgnores, activeAPICall]);

  return (
    <Card className="adOpsCard">
      <div className="adOpsCardTitle">Streaming Mappings</div>
      <div className="adOpsCardSubtitle">
        Select from Mapping/Ignore dropdown prior to importing
      </div>
      <div className="adOpsElem">
        <Dropdown
          design="primary"
          placeholder="Select Mapping or Ignore"
          value={dial || ""}
          options={[Dial.MAPPING, Dial.IGNORE]}
          onChange={val => {
            if (val === Dial.MAPPING) {
              setTradeDeskIgnores(new Map());
              setAmazonIgnores(new Map());
              setFailedIgnores([]);
              setFile(undefined);
              setDial(Dial.MAPPING);
            } else if (val === Dial.IGNORE) {
              setDerivedIdMappings(new Map());
              setIsciMappings(new Map());
              setFailedMappings([]);
              setFile(undefined);
              setDial(Dial.IGNORE);
            } else {
              setDial(null);
            }
          }}
        ></Dropdown>
      </div>
      <div className="adOpsElem">
        <Button
          type={ButtonType.FILLED}
          design="secondary"
          disabled={dial === null}
          onClick={() => setShowBulkImportModal(true)}
        >
          Import
        </Button>
      </div>
      <div className="adOpsElem">
        <Button
          type={ButtonType.FILLED}
          onClick={dial === Dial.MAPPING ? addStreamingMapping : addStreamingIgnore}
          disabled={
            dial === null ||
            (dial === Dial.MAPPING && !derivedIdMappings.size && !isciMappings.size) ||
            (dial === Dial.IGNORE && !tradeDeskIgnores.size && !amazonIgnores.size)
          }
        >
          {activeAPICall ? <Spinner animation={"border"} /> : "Submit"}
        </Button>
      </div>
      {showBulkImportModal && (
        <ImportModal
          importFunction={importMappingData}
          file={file}
          setFile={selectedFile => {
            if (!selectedFile || !selectedFile.name.endsWith(".xlsx")) {
              setError({
                message: "File must be a .xlsx",
                reportError: new Error("File must be a .xlsx"),
              });
            } else {
              setFile(selectedFile);
            }
          }}
          onClose={() => setShowBulkImportModal(false)}
        />
      )}
    </Card>
  );
};

export default StreamingMappingAdder;
